import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getLeagueTierInfo } from "@/lib/leagueTiers";
import { FormDisplay } from "./FormDisplay";
import { ClubCrest } from "@/components/ClubCrest";
import { ManagerAvatar } from "@/components/ManagerAvatar";
import { getMpiTierDecimal } from "@/lib/mpiTiers";
import { useFirstTrackedMatch } from "@/hooks/useFirstTrackedMatch";
import { buildTenureClarifier } from "@/lib/tenureClarifier";
import { differenceInMonths, differenceInDays, format } from "date-fns";
import { Link } from "react-router-dom";
import { leagueCodeToSlug } from "@/hooks/usePressureRankings";
import { GitCompare, Share2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";


interface ManagerProfileHeaderProps {
  managerId: string;
}

function formatTenure(startDate: string, endDate: string | null): string {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const months = differenceInMonths(end, start);
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years === 0) {
    const days = differenceInDays(end, start);
    if (days < 30) return `${days} days`;
    return `${remainingMonths} month${remainingMonths !== 1 ? "s" : ""}`;
  }
  if (remainingMonths === 0) return `${years} year${years !== 1 ? "s" : ""}`;
  return `${years}y ${remainingMonths}m`;
}

function ordinal(n: number) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

/** Scores are stored as 0–1 decimals; older rows may already be 0–100. */
function toDecimal(raw: number) {
  const n = Number(raw);
  return n > 1 ? n / 100 : n;
}

export const ManagerProfileHeader = ({ managerId }: ManagerProfileHeaderProps) => {
  const { toast } = useToast();


  const { data: manager, isLoading: managerLoading } = useQuery({
    queryKey: ["manager-profile", managerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("managers")
        .select("id, name, nationality, slug, career_status, birth_date")
        .eq("id", managerId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: currentAssignment, isLoading: assignmentLoading } = useQuery({
    queryKey: ["manager-current-assignment", managerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manager_assignments")
        .select("id, start_date, end_date, league, club_id, clubs(name, brand_primary, brand_secondary)")
        .eq("manager_id", managerId)
        .order("start_date", { ascending: false })
        .limit(1)
        .single();
      if (error) return null;
      return data;
    },
  });

  const { data: firstTrackedMatch } = useFirstTrackedMatch(
    currentAssignment?.club_id,
    currentAssignment?.league,
    currentAssignment?.start_date,
  );
  const tenureClarifier = currentAssignment
    ? buildTenureClarifier({
        clubName: (currentAssignment.clubs as { name?: string } | null)?.name || "",
        league: currentAssignment.league,
        startDate: currentAssignment.start_date,
        firstMatchDate: firstTrackedMatch,
      })
    : null;

  const { data: mpiScore } = useQuery({
    queryKey: ["manager-mpi-header", currentAssignment?.id],
    queryFn: async () => {
      if (!currentAssignment?.id) return null;
      const { data, error } = await supabase
        .from("manager_hot_seat_scores")
        .select("hot_seat_score, current_position, expected_position, recent_form, ppg, played")
        .eq("assignment_id", currentAssignment.id)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!currentAssignment?.id && !currentAssignment?.end_date,
  });

  const isLoading = managerLoading || assignmentLoading;

  if (isLoading) {
    return (
      <section className="chrome-band rounded-lg p-4 md:p-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
      </section>
    );
  }

  if (!manager || !currentAssignment) return null;

  const isActive = !currentAssignment.end_date;
  const club = currentAssignment.clubs as any;
  const clubName = club?.name || "Unknown";
  const tenure = formatTenure(currentAssignment.start_date, currentAssignment.end_date);
  const startLabel = format(new Date(currentAssignment.start_date), "MMM yy");
  const endLabel = currentAssignment.end_date
    ? format(new Date(currentAssignment.end_date), "MMM yy")
    : "now";

  const decimal = mpiScore ? toDecimal(mpiScore.hot_seat_score) : null;
  const tier = decimal != null ? getMpiTierDecimal(decimal) : null;
  const mpi = decimal != null ? Math.round(decimal * 100) : null;
  const formArray =
    mpiScore?.recent_form?.split("").filter((c): c is "W" | "D" | "L" => ["W", "D", "L"].includes(c)) || [];

  const positionSentence = (() => {
    if (!mpiScore?.current_position) return null;
    const pos = ordinal(mpiScore.current_position);
    if (!mpiScore.expected_position) return `${pos} in the table`;
    return `${pos}, expected ${ordinal(mpiScore.expected_position)}`;
  })();



  return (
    <section className="chrome-band overflow-hidden rounded-lg">
      <div className="p-4 md:p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          {/* Identity */}
          <div className="flex min-w-0 items-center gap-3 md:gap-4">
            <ManagerAvatar
              name={manager.name}
              size="xl"
              className="shrink-0 ring-2 ring-chrome-border"
            />

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-2xl font-extrabold leading-tight tracking-[-0.02em] text-chrome-foreground md:text-4xl">
                  {manager.slug ? (
                    <Link to={`/manager/${manager.slug}`} className="hover:underline">
                      {manager.name}
                    </Link>
                  ) : (
                    manager.name
                  )}
                </h1>
                <span
                  className={
                    isActive
                      ? "rounded bg-brand px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-foreground"
                      : "rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-chrome-muted"
                  }
                >
                  {isActive
                    ? "Active"
                    : (manager as { career_status?: string }).career_status === "retired"
                      ? "Retired"
                      : "Inactive"}
                </span>
              </div>

              {(manager.nationality || (manager as { birth_date?: string | null }).birth_date) && (
                <p className="mt-0.5 text-xs text-chrome-muted">
                  {[
                    (manager as { birth_date?: string | null }).birth_date &&
                      `${format(new Date((manager as { birth_date?: string | null }).birth_date!), "d MMMM yyyy")} (${Math.floor((Date.now() - new Date((manager as { birth_date?: string | null }).birth_date!).getTime()) / (365.25 * 24 * 60 * 60 * 1000))})`,
                    manager.nationality,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-chrome-muted">
                <ClubCrest
                  clubName={clubName}
                  primary={club?.brand_primary}
                  secondary={club?.brand_secondary}
                  size="md"
                />
                <span className="font-semibold text-chrome-foreground">{clubName}</span>
                <Link
                  to={`/league/${leagueCodeToSlug(currentAssignment.league)}`}
                  className="rounded border border-chrome-border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-chrome-foreground transition-colors hover:border-brand hover:text-brand"
                  title="League pressure rankings"
                >
                  {getLeagueTierInfo(currentAssignment.league).label}
                </Link>
                <span aria-hidden>·</span>
                <span className="num whitespace-nowrap">
                  {startLabel} – {endLabel} ({tenure})
                </span>
              </div>
              {tenureClarifier && (
                <p className="mt-1.5 text-xs leading-snug text-chrome-muted">
                  {tenureClarifier.headerLine}
                </p>
              )}
            </div>
          </div>

          {/* MPI block */}
          {mpi != null && tier && isActive && (
            <div className="flex items-center gap-3 rounded-md border border-chrome-border bg-white/[0.04] p-3 md:min-w-[220px] md:flex-col md:items-start">
              <div className="flex items-baseline gap-2">
                <span
                  className="num font-display text-4xl font-extrabold leading-none md:text-5xl"
                  style={{ color: tier.color }}
                >
                  {mpi}
                </span>
                <span className="label-micro text-chrome-muted">MPI</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-chrome-foreground">{tier.label}</p>
                {positionSentence && (
                  <p className="text-xs text-chrome-muted">{positionSentence}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Secondary stats + actions */}
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-chrome-border pt-3">
          {mpiScore?.ppg != null && mpiScore.played > 0 && (
            <div className="text-sm text-chrome-muted">
              <span className="num font-semibold text-chrome-foreground">{mpiScore.ppg.toFixed(2)}</span> PPG
            </div>
          )}
          {mpiScore?.played != null && mpiScore.played > 0 && (
            <div className="text-sm text-chrome-muted">
              <span className="num font-semibold text-chrome-foreground">{mpiScore.played}</span> league games
            </div>

          )}
          {formArray.length > 0 && <FormDisplay form={formArray} variant="letters" size="sm" />}

          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const url = window.location.href;
                const title = `${manager.name} — ManagerTable`;
                if (navigator.share) {
                  try {
                    await navigator.share({ title, url });
                    return;
                  } catch {
                    /* user cancelled — fall through to copy */
                  }
                }
                try {
                  await navigator.clipboard.writeText(url);
                  toast({ title: "Link copied", description: "Manager page link is on your clipboard." });
                } catch {
                  toast({ title: "Could not copy link", variant: "destructive" });
                }
              }}
              className="min-h-[40px] border-chrome-border bg-white/[0.06] text-chrome-foreground hover:bg-white/[0.14] hover:text-chrome-foreground"
            >
              <Share2 className="mr-1.5 h-4 w-4" />
              Share
            </Button>
            <Button
              variant="outline"
              size="sm"
              asChild
              className="min-h-[40px] border-chrome-border bg-white/[0.06] text-chrome-foreground hover:bg-white/[0.14] hover:text-chrome-foreground"
            >
              <Link to={`/compare?manager1=${managerId}`}>
                <GitCompare className="mr-1.5 h-4 w-4" />
                Compare
              </Link>
            </Button>
          </div>

        </div>
      </div>
    </section>
  );
};
